import { CalendarApi, EventApi } from '@fullcalendar/core';
import { TranslateService } from '@ngx-translate/core';
import { AppInjector } from '@shared/services/app-injector.service';
import { ProjectStage } from '@shared/enums/project-stage.enum';
import { Project } from '@modules/projects/shared/models/project.model';
import { IProjectStage } from '@modules/projects/shared/interfaces/project-stage.interface';
import { ProjectsTimelineEventController } from './projects-timeline-event.controller';
import * as moment from 'moment';
import { ProjectTimelineStage } from '@modules/projects/shared/interfaces/timeline.interface';

export class ProjectsTimelineResourceController {
  t: TranslateService;
  eventController: ProjectsTimelineEventController;

  constructor(private calendar: CalendarApi) {
    this.t = AppInjector.getInjector().get(TranslateService);
    this.eventController = new ProjectsTimelineEventController(this.calendar);
  }

  private getTimelineStageTitle(stage: { timelineStage?: { translation?: string; name?: string } }): string {
    const translation = stage?.timelineStage?.translation || '';
    if (translation.startsWith('timeline_stages.')) {
      return stage?.timelineStage?.name || '';
    }

    return translation || stage?.timelineStage?.name || '';
  }

  addProjectsResources(projects: Project[]) {
    for (let i = 0; i < projects.length; i++) {
      const project = new Project(projects[i]);
      this.addProjectResource(project, i);
    }
  }

  addStagesResources(stages: IProjectStage[]) {
    for (let i = 0; i < stages.length; i++) {
      this.addStageResource(stages[i]);
    }
  }

  updateResourcesAfterEventChange(project: Project, newStage: ProjectStage, projectEvent: EventApi) {
    const projectResource = this.calendar.getResourceById(project.id.toString());
    const newStageResource = this.calendar.getResourceById(newStage);
    const oldStageResource = this.calendar.getResourceById(project.stage);
    !projectEvent ? (projectEvent = this.calendar.getEventById(project.id.toString())) : '';

    projectResource.setProp('parentId', newStage);

    newStageResource.setExtendedProp(
      'projectsCount',
      newStageResource._resource.extendedProps.projectsCount + 1
    );

    oldStageResource.setExtendedProp(
      'projectsCount',
      oldStageResource._resource.extendedProps.projectsCount - 1
    );

    if (newStageResource._resource.extendedProps.opened) {
      projectEvent?.setExtendedProp('stage', newStage);
      projectEvent?.setResources([project.id.toString()]);
      projectEvent?.setProp('classNames', 'fc-event--' + newStage + ' fc-event--project-event');
      this.eventController.updateOpenedStageEvent(newStage);
    } else {
      this.eventController.updateClosedStageEvent(
        newStage,
        projectEvent?.start ? moment(projectEvent?.start) : null,
        projectEvent?.end ? moment(projectEvent?.end) : null
      );
      projectResource.remove();
      projectEvent?.remove();
    }

    oldStageResource._resource.extendedProps.projectsCount === 0
      ? oldStageResource.setExtendedProp('opened', false)
      : null;

    oldStageResource._resource.extendedProps.opened
      ? this.eventController.updateOpenedStageEvent(project.stage)
      : this.eventController.updateClosedStageEvent(project.stage, null, null);
  }

  private addProjectResource(project: Project, index: number) {
    if (this.calendar.getResourceById(project.id.toString())) return;

    this.calendar.addResource({
      parentId: project.stage,
      id: project.id.toString(),
      title: project.fullName,
      extendedProps: project,
      index
    });

    this.calendar.addEvent({
      resourceId: project.id.toString(),
      id: `pause-${project.id}`,
      title: this.t.instant('Projects.Pause.title'),
      start: moment(project.basicDataBox.pauseStart).toDate(),
      end: moment(project.basicDataBox.pauseEnd).add(1, 'day').startOf('day').toDate(),
      classNames: ['pause-overlay'],
      extendedProps: { ...project, type: 'pause' },
      editable: true,
      eventStartEditable: true,
      eventDurationEditable: true
    });

    project.basicDataBox.activeTimelineStages?.forEach((stage: ProjectTimelineStage) => {
      this.calendar.addEvent({
        resourceId: project.id.toString(),
        id: `stage-${stage.timelineStage.id.toString()}`,
        title: this.getTimelineStageTitle(stage),
        start: moment(stage.startDate).toDate(),
        end: moment(stage.endDate).add(1, 'day').startOf('day').toDate(),
        classNames: ['stage-overlay'],
        extendedProps: { ...project, type: 'stage', color: stage.timelineStage.color },
        editable: true,
        eventStartEditable: true,
        eventDurationEditable: true
      });
    });
  }

  private addStageResource(stage: IProjectStage) {
    if (this.calendar.getResourceById(stage.stage)) return;

    console.log(stage.stage);
    this.calendar.addResource({
      id: stage.stage,
      title: this.t.instant('Projects.Stage.' + stage.stage),
      opened: false,
      extendedProps: stage
    });
  }

  updateProjectResource(project: Project) {
    const projectResource = this.calendar.getResourceById(project.id.toString());
    projectResource.setExtendedProp('basicDataBox', project.basicDataBox);
    projectResource.setExtendedProp('town', project.town);
    projectResource.setExtendedProp('postalCode', project.postalCode);
    projectResource.setExtendedProp('country', project.country);
    projectResource.setExtendedProp('fullAddress', project.fullAddress);
    projectResource.setExtendedProp('fullName', project.fullName);
    projectResource.setExtendedProp('creator', project.creator);
    projectResource.setExtendedProp('projectId', project.projectId);
    projectResource.setExtendedProp('stage', project.stage);
    projectResource.setProp('parentId', project.stage);
  }

  removeProjectsResourcesFromStage(stage: ProjectStage) {
    const resources = this.calendar.getResources();

    for (let i = 0; i < resources.length; i++) {
      const r = resources[i];
      if (r._resource.parentId === stage) {
        r.remove();
      }
    }
  }

  removeAllResources(): void {
    this.calendar.setOption('resources', []);
    const resources = this.calendar.getResources();
    resources.forEach((resource) => resource.remove());
  }
}
